// Spending — what Rootegra spent this year by account, a form for a one-off
// expense, and the list of expense and shared-cost entries.
import { useEffect, useMemo, useState } from 'react';
import { useBooksAccess } from '../../contexts/AuthContext';
import { Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day, dollars } from '../../lib/format';
import { journal, reports, vendors as vendorsApi } from '../../lib/ledger/api';
import { SOURCE_LABELS } from '../../lib/ledger/types';
import type { Account, JournalEntry, Vendor } from '../../lib/ledger/types';
import { buildProfitLoss } from '../../lib/ledger/reports';
import { AccountSelect, Money, MoneyInput, ReadOnlyNote, downloadCsv, today, useAccounts, yearStart } from './shared';

function useLoad<T>(fn: () => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

/** Where an expense can be paid from: cash-like assets, cards and amounts owed. */
function paidFromCandidates(accounts: Account[]): Account[] {
  return accounts.filter((a) =>
    (a.type === 'asset' && (a.subtype === 'bank' || a.subtype === 'vessel'))
    || (a.type === 'liability' && ['credit_card', 'due_to_member', 'related_party', 'accounts_payable'].includes(a.subtype)));
}

function entryParts(e: JournalEntry) {
  const debit = e.lines.find((l) => Number(l.debit_cents) > 0);
  const credit = e.lines.find((l) => Number(l.credit_cents) > 0);
  const amount = e.lines.reduce((s, l) => s + Number(l.debit_cents), 0);
  return { debit, credit, amount };
}

export function SpendingTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const from = yearStart();
  const to = today();
  const year = from.slice(0, 4);
  const tb = useLoad(() => reports.trialBalance(from, to), [from, to]);
  const vendorList = useLoad(() => vendorsApi.list(), []);
  const entries = useLoad(async () => {
    const [a, b] = await Promise.all([
      journal.page({ from, to, source: 'expense', limit: 500 }),
      journal.page({ from, to, source: 'shared_cost', limit: 500 }),
    ]);
    return [...a.entries, ...b.entries].sort((x, y) => (y.entry_date.localeCompare(x.entry_date)) || (y.created_at ?? '').localeCompare(x.created_at ?? ''));
  }, [from, to]);
  const pl = useMemo(() => (tb.data ? buildProfitLoss(tb.data) : null), [tb.data]);
  const vendors: Vendor[] = vendorList.data ?? [];
  const vendorById = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);

  const [form, setForm] = useState({ date: today(), amount: null as number | null, expense_account_id: '', paid_from_account_id: '', vendor_id: '', memo: '', receipt_url: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  const ready = form.date && form.amount != null && form.amount > 0 && form.expense_account_id && form.paid_from_account_id;

  async function record() {
    if (!ready || form.amount == null) return;
    setBusy(true); setErr(null); setDone(null);
    try {
      await journal.recordExpense({
        date: form.date, amount_cents: form.amount, expense_account_id: form.expense_account_id, paid_from_account_id: form.paid_from_account_id,
        vendor_id: form.vendor_id || null, memo: form.memo.trim(), receipt_url: form.receipt_url.trim() || null,
      });
      setDone(`Recorded ${dollars(form.amount)} to ${chart.byId.get(form.expense_account_id)?.name ?? 'expenses'}.`);
      setForm({ ...form, amount: null, memo: '', receipt_url: '' });
      tb.reload();
      entries.reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  const list = entries.data ?? [];
  const vendorName = (e: JournalEntry) => {
    const id = e.details?.vendor_id;
    if (typeof id === 'string' && vendorById.get(id)) return vendorById.get(id)!.name;
    const slug = e.details?.vendor ?? e.details?.vendor_slug;
    if (typeof slug === 'string') return vendors.find((v) => v.slug === slug)?.name ?? slug.replace(/_/g, ' ');
    return '';
  };
  const exportCsv = () => {
    downloadCsv(`rootegra-spending-${year}.csv`, [
      ['Date', 'Source', 'Memo', 'Vendor', 'Expense account', 'Paid from', 'Amount', 'Receipt'],
      ...list.map((e) => {
        const p = entryParts(e);
        return [e.entry_date, SOURCE_LABELS[e.source] ?? e.source, e.memo ?? '', vendorName(e), p.debit ? `${p.debit.code ?? ''} ${p.debit.name ?? ''}`.trim() : '', p.credit ? `${p.credit.code ?? ''} ${p.credit.name ?? ''}`.trim() : '', (p.amount / 100).toFixed(2), String(e.details?.receipt_url ?? '')];
      }),
    ]);
  };

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      <ErrorNote error={chart.error} />
      <ErrorNote error={tb.error} />
      <div className="kpi-grid">
        <div className="kpi"><div className="kpi-label">Expenses, {year} to date</div><div className="kpi-num">{pl ? dollars(pl.expenses.total_cents) : '—'}</div><div className="kpi-sub">Everything below cost of goods</div></div>
        <div className="kpi"><div className="kpi-label">Net income</div><div className="kpi-num">{pl ? dollars(pl.net_income_cents) : '—'}</div><div className="kpi-sub">Income minus cost of goods and expenses</div></div>
      </div>

      <Section title={`By account, ${year}`}>
        {tb.loading ? <Empty>Loading…</Empty> : !pl || pl.expenses.rows.length === 0 ? <Empty>No expenses recorded yet this year.</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Account</th><th className="num">{year} to date</th></tr></thead>
              <tbody>
                {pl.expenses.rows.map((r) => <tr key={r.account_id}><td>{r.code} · {r.name}</td><td className="num"><Money cents={r.amount_cents} /></td></tr>)}
                <tr className="total"><td>Total expenses</td><td className="num"><Money cents={pl.expenses.total_cents} /></td></tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Record an expense">
        <ErrorNote error={err} />
        <ErrorNote error={vendorList.error} />
        {done && <Note tone="good">{done}</Note>}
        <div className="lp-form-grid">
          <Field label="Date"><input className="lp-inp" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={!canEdit} /></Field>
          <Field label="Amount"><MoneyInput cents={form.amount} onChange={(c) => setForm({ ...form, amount: c })} disabled={!canEdit} /></Field>
          <Field label="Vendor" hint="Picking one fills in its usual account.">
            <select
              className="lp-inp"
              value={form.vendor_id}
              disabled={!canEdit}
              onChange={(e) => {
                const v = vendorById.get(e.target.value);
                setForm({ ...form, vendor_id: e.target.value, expense_account_id: v?.default_account_id ?? form.expense_account_id });
              }}
            >
              <option value="">No vendor</option>
              {vendors.filter((v) => v.is_active || v.id === form.vendor_id).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>
          <Field label="Expense account"><AccountSelect value={form.expense_account_id} onChange={(id) => setForm({ ...form, expense_account_id: id })} accounts={chart.accounts} types={['expense', 'cogs']} disabled={!canEdit} /></Field>
          <Field label="Paid from"><AccountSelect value={form.paid_from_account_id} onChange={(id) => setForm({ ...form, paid_from_account_id: id })} accounts={paidFromCandidates(chart.accounts)} disabled={!canEdit} /></Field>
          <Field label="Memo"><input className="lp-inp" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} disabled={!canEdit} placeholder="What it was for" /></Field>
          <Field label="Receipt link"><input className="lp-inp" type="url" value={form.receipt_url} onChange={(e) => setForm({ ...form, receipt_url: e.target.value })} disabled={!canEdit} placeholder="https://" /></Field>
        </div>
        <div className="lp-row">
          <button type="button" className="lp-btn lp-btn--primary" disabled={!canEdit || busy || !ready} onClick={() => void record()}>{busy ? 'Recording…' : 'Record expense'}</button>
        </div>
      </Section>

      <Section title="Recorded this year" right={<button type="button" className="lp-btn lp-btn--quiet lp-no-print" disabled={list.length === 0} onClick={exportCsv}>CSV</button>}>
        <Note>Shared costs come from the Apple tab: each month's hosting and software bills, split at the vendor's Rootegra share. They post to the ledger when the month closes.</Note>
        <ErrorNote error={entries.error} />
        {entries.loading ? <Empty>Loading…</Empty> : list.length === 0 ? <Empty>No expenses or shared costs recorded this year.</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Date</th><th>Source</th><th>Memo</th><th>Vendor</th><th>Expense account</th><th>Paid from</th><th className="num">Amount</th><th>Receipt</th></tr></thead>
              <tbody>
                {list.map((e) => {
                  const p = entryParts(e);
                  const receipt = e.details?.receipt_url;
                  return (
                    <tr key={e.id} className={e.is_reversed ? 'quiet' : undefined}>
                      <td>{day(e.entry_date)}</td>
                      <td><span className="lp-tag">{SOURCE_LABELS[e.source] ?? e.source}</span>{e.is_reversed && <span className="lp-tag lp-tag--warn" style={{ marginLeft: 6 }}>Reversed</span>}</td>
                      <td>{e.memo || '—'}</td>
                      <td>{vendorName(e) || '—'}</td>
                      <td>{p.debit ? `${p.debit.code ?? ''} · ${p.debit.name ?? chart.byId.get(p.debit.account_id)?.name ?? ''}` : '—'}</td>
                      <td>{p.credit ? `${p.credit.code ?? ''} · ${p.credit.name ?? chart.byId.get(p.credit.account_id)?.name ?? ''}` : '—'}</td>
                      <td className="num"><Money cents={p.amount} /></td>
                      <td>{typeof receipt === 'string' && receipt ? <a href={receipt} target="_blank" rel="noreferrer">Open</a> : <span className="muted">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}
